/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { featureCollection } from '@turf/helpers';
import { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import get from 'lodash/get';
import { Action, ActionCreators } from './actions';

interface HeatMaps {
  flHeatMapContours: FeatureCollection<MultiPolygon | Polygon>;
  rlHeatMapContours: FeatureCollection<MultiPolygon | Polygon>;
}

export interface State {
  beams: FeatureCollection<MultiPolygon | Polygon>;
  heatMaps: HeatMaps;
  isRequestingBeams: boolean;
  isBeamsRequestFinished: boolean;
  flRlContoursAvailable: boolean;
  requestBeamsError?: string;
  isRequestingFlHeatMaps: boolean;
  isFlHeatMapsRequestFinished: boolean;
  requestFlHeatMapsError?: string;
  isRequestingRlHeatMaps: boolean;
  isRlHeatMapsRequestFinished: boolean;
  requestRlHeatMapsError?: string;
}

export const initialBeamsState: State = {
  beams: featureCollection([]),
  heatMaps: {
    flHeatMapContours: featureCollection([]),
    rlHeatMapContours: featureCollection([])
  },
  isRequestingBeams: false,
  isBeamsRequestFinished: false,
  requestBeamsError: undefined,
  flRlContoursAvailable: false,
  isRequestingFlHeatMaps: false,
  requestFlHeatMapsError: undefined,
  isFlHeatMapsRequestFinished: false,
  isRequestingRlHeatMaps: false,
  requestRlHeatMapsError: undefined,
  isRlHeatMapsRequestFinished: false,
};

export default function reducer(state: State = initialBeamsState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    case ActionCreators.RequestBeams.type:
      partialState = {
        beams: featureCollection<MultiPolygon | Polygon>([]),
        flRlContoursAvailable: false,
        isRequestingBeams: true,
        isBeamsRequestFinished: false,
        requestBeamsError: undefined,
      };
      break;

    case ActionCreators.ReceivedBeamsSuccess.type: {
      const versionsWithHeatmap = get(action.payload.response, 'properties.versionsWithHeatmap', []);
      partialState = {
        beams: { ...action.payload.response },
        flRlContoursAvailable: versionsWithHeatmap.length > 0,
        isRequestingBeams: false,
        isBeamsRequestFinished: true,
        requestBeamsError: undefined,
      };

      break;
    }

    case ActionCreators.ReceivedBeamsError.type:
      partialState = {
        isRequestingBeams: false,
        flRlContoursAvailable: false,
        isBeamsRequestFinished: true,
        requestBeamsError: get(action, 'payload.response.error_description', 'Unknown error'),
      };
      break;
    case ActionCreators.RequestFlHeatMaps.type:
      partialState = {
        heatMaps: {
          ...state.heatMaps,
          flHeatMapContours: featureCollection([]),
        },
        isRequestingFlHeatMaps: true,
        isFlHeatMapsRequestFinished: false,
        requestFlHeatMapsError: undefined,
      };
      break;
    case ActionCreators.RequestRlHeatMaps.type:
      partialState = {
        heatMaps: {
          ...state.heatMaps,
          rlHeatMapContours: featureCollection([]),
        },
        isRequestingRlHeatMaps: true,
        isRlHeatMapsRequestFinished: false,
        requestRlHeatMapsError: undefined,
      };
      break;
    case ActionCreators.ReceivedFlHeatMapsSuccess.type: {
      partialState = {
        heatMaps: {
          ...state.heatMaps,
          flHeatMapContours: action.payload.response,
        },
        isRequestingFlHeatMaps: false,
        isFlHeatMapsRequestFinished: true,
        requestFlHeatMapsError: undefined,
      };
      break;
    }
    case ActionCreators.ReceivedRlHeatMapsSuccess.type: {
      partialState = {
        heatMaps: {
          ...state.heatMaps,
          rlHeatMapContours: action.payload.response,
        },
        isRequestingRlHeatMaps: false,
        isRlHeatMapsRequestFinished: true,
        requestRlHeatMapsError: undefined,
      };
      break;
    }
    case ActionCreators.ReceivedFlHeatMapsError.type:
      partialState = {
        isRequestingFlHeatMaps: false,
        isFlHeatMapsRequestFinished: true,
        requestFlHeatMapsError: get(action, 'payload.response.error_description', 'Unknown error'),
      };
      break;
    case ActionCreators.ReceivedRlHeatMapsError.type:
      partialState = {
        isRequestingRlHeatMaps: false,
        isRlHeatMapsRequestFinished: true,
        requestRlHeatMapsError: get(action, 'payload.response.error_description', 'Unknown error'),
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
